import React from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import {
  Element,
  Color,
  Size,
  Breakpoint,
  Typography,
  Icon,
  HowYouEarnTiles
} from '@americanexpress/aqx-components';
import Flag from '../Flag';
import { toggleModalByName } from 'src/universal/components/Modal';
import { getPdpHowYouEarnData } from '../../redux';
import { getPageFeatureFlags } from 'src/universal/redux/page';
import { Tooltip } from 'src/universal/components/ToolTip/index';
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';

import styles from './__styles__/index.scss?modules';

const InfoIcon = ({ onClick }) => (
  <div onClick={onClick}>
    <Icon
      directory="dls"
      imageName="dls-icon-info"
      $size={Size.MD}
      theme={{
        color: Color.BrightBlue
      }}
    />
  </div>
);

function HowYouEarn({ data, featureFlags }) {
  const { header, tooltip, slides, dtm, disclaimer, flag } = data;
  if (!header || !slides || !dtm) {
    return null;
  }

  const tileCount = slides.length;
  const showFirstTileAsCrossHeader = tileCount > 3;

  const slidesWithPopoverCallbacks = slides.map(slide => {
    if (
      featureFlags?.showKaratFeature &&
      slide.tag &&
      typeof slide.text === 'string'
    ) {
      const textVal = slide.text;
      slide.text = (
        <Element className={styles.textWrap}>
          <Sanitizer Component="span" safelySetInnerHTML={textVal} />
          <Element className={styles.contentTag} Component="div">
            <span>{slide.tag}</span>
          </Element>
        </Element>
      );
    }

    return slide;
  });
  const headerTooltip = tooltip ? (
    <Element
      theme={{
        marginLeft: 10
      }}
    >
      <Breakpoint>
        {({ sm } = {}) =>
          sm ? (
            <InfoIcon onClick={() => toggleModalByName('HOWYOUEARN-HEADER-MODAL')} />
          ) : (
            <Tooltip
              useNewCloseIcon
              centerPopupArrow
              increasedPadding
              clickableElement={<InfoIcon />}
              tooltipPopupText={tooltip.text}
              getCustomizedPosition={(toolTipRect, yOffset, xOffset) => {
                const howYouEarnRect = document
                  .querySelector(
                    'div[class*="purify_index__howYouEarnModule--"] h2[class*="purify_index__sectionTitle--"]'
                  )
                  ?.getBoundingClientRect();
                /*
                  * if I take away from the yOffset it goes down in the screen
                  * if I add to yOffset it goes up in the screen
                */
                if (toolTipRect.top <= howYouEarnRect.bottom) {
                  const diff = howYouEarnRect.bottom - toolTipRect.top;
                  yOffset -= diff;
                }

                return {
                  newX: xOffset,
                  newY: yOffset
                };
              }}
            />
          )
        }
      </Breakpoint>
    </Element>
  ) : null;

  const hasContentTag =
    featureFlags?.showKaratFeature && (slides || []).find(s => !!s.tag);

  return (
    <Element
      id="gcsPdpHowYouEarn"
      className={styles.howYouEarnModule}
      theme={{
        marginBottom: 30
      }}
    >
      <Element
        className={styles.header}
        theme={{
          marginBottom: 20,
          paddingVertical: 5
        }}
      >
        <Typography Component="h2" className={styles.sectionTitle}>
          {header}
        </Typography>
        {headerTooltip}
        {flag && flag.text && <Flag className={styles.flag} text={flag.text} />}
      </Element>
      <LazyScrollMetricTracker
        scrollDtm={dtm.howYouEarnImpression}
        startTrackingAfterPznArrival={true}
      />
      <div
        className={clsx(styles.howYouEarnBenefits, styles[`tileCnt${tileCount}`], {
          [styles.hasContentTag]: hasContentTag
        })}
      >
        <HowYouEarnTiles
          customization={{
            headerColor: Color.DeepBlue,
            subHeaderColor: Color.DeepBlue,
            titleColor: Color.DeepBlue,
            textColor: Color.Gray06
          }}
          showFloatingBanner={showFirstTileAsCrossHeader}
          data={{ slides: slidesWithPopoverCallbacks }}
          $border={false}
        />
      </div>
      {disclaimer && (
        <Typography
          $size="85%"
          theme={{ marginTop: 10, marginHorizontal: 5 }}
          dangerouslySetInnerHTML={{ __html: disclaimer }}
        />
      )}
    </Element>
  );
}

const mapStateToProps = state => ({
  data: getPdpHowYouEarnData(state),
  featureFlags: getPageFeatureFlags(state)
});

export default connect(mapStateToProps)(HowYouEarn);
